import { memo, useMemo } from 'react';

import { Line } from '@react-three/drei';
import * as THREE from 'three';

import { colors } from '@ui';

import { createDirectionalLine, createMidPointDirectionalLine, getKeypointId, getSafePoint } from '../helpers';

import type { FrameFirstSkeleton } from '../skeletonUtils';
import type { KeypointIdToName } from '../types';

type ConnectionType = 'extended' | 'connected';

type VisualizationCalculation = {
    directionalLine: [THREE.Vector3, THREE.Vector3] | [THREE.Vector3, THREE.Vector3, THREE.Vector3];
    referenceLine: [THREE.Vector3, THREE.Vector3];
    triangleBuffer: Float32Array;
};

type PointMatrix = [number, number, number][];

type AngleVisualizationProps = {
    type: ConnectionType;
    skeleton: FrameFirstSkeleton;
    frame?: number;
    targetPoints: string[];
    keypointIdToName: KeypointIdToName;
    currentFocus: { measurement: string; axis: number } | null;
    opacity?: number;
    showFill?: boolean;

    /**
     * Optional explicit angle geometry.
     *
     * When all three are provided, the visualization draws the angle directly
     * from these vectors instead of deriving geometry from skeleton keypoints.
     *
     * This is useful for measurements involving geometry outside the skeleton,
     * such as the golf club.
     */
    pivot?: THREE.Vector3;
    referenceDirection?: THREE.Vector3;
    actualDirection?: THREE.Vector3;
};

type VisualizationProps = {
    type: ConnectionType;
    points: PointMatrix;
    currentFrame: number;
    opacity?: number;
    showFill?: boolean;
    referenceDirection?: THREE.Vector3;
    actualDirection?: THREE.Vector3;
};

const TRIANGLE_INDICES = new Uint16Array([0, 1, 2]);
const SHARED_TRIANGLE_POSITION_BUFFER = new Float32Array(9);

// Default reference directions for each axis (global coordinates)
const DEFAULT_REFERENCE_DIRECTIONS = {
    tilt: new THREE.Vector3(0, 0, 1), // Z axis
    bend: new THREE.Vector3(0, 0, 1), // Z axis
    rotation: new THREE.Vector3(0, 1, 0), // Y axis
};

function createTriangleBuffer(vertices: THREE.Vector3[]) {
    const buffer = SHARED_TRIANGLE_POSITION_BUFFER;
    let i = 0;

    for (const vertex of vertices) {
        buffer[i++] = vertex.x;
        buffer[i++] = vertex.y;
        buffer[i++] = vertex.z;
    }

    return buffer;
}

/**
 * Get the reference direction as an array, using provided direction or falling back to default.
 */
function getRefDir(referenceDirection: THREE.Vector3 | undefined, defaultDir: THREE.Vector3): [number, number, number] {
    const dir = referenceDirection ?? defaultDir;
    return [dir.x, dir.y, dir.z];
}

/**
 * Draw an angle from two explicitly supplied 3D directions.
 *
 * Unlike the existing tilt/bend/rotation visualizations, this does not derive
 * geometry from targetPoints and does not attach semantic meaning to axisIndex.
 *
 * Both directions are translated to the supplied pivot. Translation does not
 * affect the angle between the vectors.
 */
const calculateDirectionAngleLines = (
    pivot: THREE.Vector3,
    referenceDirection: THREE.Vector3,
    actualDirection: THREE.Vector3,
): VisualizationCalculation => {
    const refDir = referenceDirection.clone().normalize();
    const actualDir = actualDirection.clone().normalize();

    const lineScale = 10;

    const referenceLineStart = pivot.clone().add(refDir.clone().multiplyScalar(-lineScale));
    const referenceLineEnd = pivot.clone().add(refDir.clone().multiplyScalar(lineScale));

    const directionalLineStart = pivot.clone().add(actualDir.clone().multiplyScalar(-lineScale));
    const directionalLineEnd = pivot.clone().add(actualDir.clone().multiplyScalar(lineScale));

    const triangleScale = 0.15;
    const triangleVertices = [
        pivot.clone(),
        pivot.clone().add(actualDir.clone().multiplyScalar(triangleScale)),
        pivot.clone().add(refDir.clone().multiplyScalar(triangleScale)),
    ];

    return {
        directionalLine: [directionalLineStart, directionalLineEnd],
        referenceLine: [referenceLineStart, referenceLineEnd],
        triangleBuffer: createTriangleBuffer(triangleVertices),
    };
};

// Two point rotation angle
const calculateTwoPointRotationLines = (
    points: PointMatrix,
    referenceDirection?: THREE.Vector3,
    actualDirection?: THREE.Vector3,
): VisualizationCalculation => {
    const refDir = getRefDir(referenceDirection, DEFAULT_REFERENCE_DIRECTIONS.rotation);

    // Use actualDirection from Euler angles when provided, otherwise compute from keypoints
    const actualDir: [number, number, number] = actualDirection
        ? [actualDirection.x, actualDirection.y, actualDirection.z]
        : refDir;

    const [directionalLineStart, directionalLineEnd] = createDirectionalLine([points[0], points[1]], actualDir, true);
    const [referenceLineStart, referenceLineEnd] = createMidPointDirectionalLine([points[0], points[1]], refDir, false);

    const triangleVertices = [referenceLineStart, directionalLineStart, referenceLineEnd];

    return {
        directionalLine: [directionalLineStart, directionalLineEnd],
        referenceLine: [referenceLineStart, referenceLineEnd],
        triangleBuffer: createTriangleBuffer(triangleVertices),
    };
};

// Two point tilt angle
const calculateTwoPointTiltLines = (
    points: PointMatrix,
    referenceDirection?: THREE.Vector3,
    actualDirection?: THREE.Vector3,
): VisualizationCalculation => {
    // When Euler-derived directions are provided, use them for both lines
    if (referenceDirection && actualDirection) {
        // Pivot point is the first keypoint
        const pivot = new THREE.Vector3(...points[0]);

        const refDir = referenceDirection.clone().normalize();
        const actualDir = actualDirection.clone().normalize();

        // Large scale so lines extend visually
        const lineScale = 10;

        // Reference line from pivot in reference direction
        const referenceLineStart = pivot.clone().add(refDir.clone().multiplyScalar(-lineScale));
        const referenceLineEnd = pivot.clone().add(refDir.clone().multiplyScalar(lineScale));

        // Directional line from pivot in actual direction
        const directionalLineStart = pivot.clone().add(actualDir.clone().multiplyScalar(-lineScale));
        const directionalLineEnd = pivot.clone().add(actualDir.clone().multiplyScalar(lineScale));

        const triangleScale = 0.15;
        const triangleVertices = [
            pivot.clone(),
            pivot.clone().add(actualDir.clone().multiplyScalar(triangleScale)),
            pivot.clone().add(refDir.clone().multiplyScalar(triangleScale)),
        ];

        return {
            directionalLine: [directionalLineStart, directionalLineEnd],
            referenceLine: [referenceLineStart, referenceLineEnd],
            triangleBuffer: createTriangleBuffer(triangleVertices),
        };
    }

    const refDir = getRefDir(referenceDirection, DEFAULT_REFERENCE_DIRECTIONS.tilt);
    const actualDir: [number, number, number] = actualDirection
        ? [actualDirection.x, actualDirection.y, actualDirection.z]
        : [0, 1, 0];

    const [directionalLineStart, directionalLineEnd] = createDirectionalLine([points[0], points[1]], actualDir, true);
    const [referenceLineStart, referenceLineEnd] = createMidPointDirectionalLine([points[1], points[0]], refDir, false);

    const triangleVertices = [referenceLineStart, directionalLineStart, referenceLineEnd];

    return {
        directionalLine: [directionalLineStart, directionalLineEnd],
        referenceLine: [referenceLineStart, referenceLineEnd],
        triangleBuffer: createTriangleBuffer(triangleVertices),
    };
};

// Two point bend angle
const calculateTwoPointBendLines = (
    points: PointMatrix,
    referenceDirection?: THREE.Vector3,
    actualDirection?: THREE.Vector3,
): VisualizationCalculation => {
    // When Euler-derived directions are provided, use them for both lines
    // This ensures the displayed angle matches the measurement value
    if (referenceDirection && actualDirection) {
        // Pivot point is the first keypoint (e.g., upper_torso for shoulder bend)
        const pivot = new THREE.Vector3(...points[0]);

        const refDir = referenceDirection.clone().normalize();
        const actualDir = actualDirection.clone().normalize();

        // Large scale so lines extend visually
        const lineScale = 10;

        // Reference line from pivot in reference direction
        const referenceLineStart = pivot.clone().add(refDir.clone().multiplyScalar(-lineScale));
        const referenceLineEnd = pivot.clone().add(refDir.clone().multiplyScalar(lineScale));

        // Directional line from pivot in actual direction
        const directionalLineStart = pivot.clone().add(actualDir.clone().multiplyScalar(-lineScale));
        const directionalLineEnd = pivot.clone().add(actualDir.clone().multiplyScalar(lineScale));

        const triangleScale = 0.15;
        const triangleVertices = [
            pivot.clone(),
            pivot.clone().add(actualDir.clone().multiplyScalar(triangleScale)),
            pivot.clone().add(refDir.clone().multiplyScalar(triangleScale)),
        ];

        return {
            directionalLine: [directionalLineStart, directionalLineEnd],
            referenceLine: [referenceLineStart, referenceLineEnd],
            triangleBuffer: createTriangleBuffer(triangleVertices),
        };
    }

    const refDir = getRefDir(referenceDirection, DEFAULT_REFERENCE_DIRECTIONS.bend);
    const actualDir: [number, number, number] = actualDirection
        ? [actualDirection.x, actualDirection.y, actualDirection.z]
        : [0, 1, 0];

    const [directionalLineStart, directionalLineEnd] = createDirectionalLine([points[0], points[1]], actualDir, true);
    const [referenceLineStart, referenceLineEnd] = createDirectionalLine([points[1]], refDir, false);

    const triangleLerp = 0.06;
    const triangleVertices = [
        referenceLineStart,
        new THREE.Vector3().lerpVectors(referenceLineStart, directionalLineStart, triangleLerp),
        new THREE.Vector3().lerpVectors(referenceLineStart, referenceLineEnd, triangleLerp),
    ];

    return {
        directionalLine: [directionalLineStart, directionalLineEnd],
        referenceLine: [referenceLineStart, referenceLineEnd],
        triangleBuffer: createTriangleBuffer(triangleVertices),
    };
};

// Three point tilt angle
const calculateThreePointTiltLines = (
    points: PointMatrix,
    type: ConnectionType,
    referenceDirection?: THREE.Vector3,
    actualDirection?: THREE.Vector3,
): VisualizationCalculation => {
    if (type === 'extended') {
        // When Euler-derived directions are provided, use them for both lines
        if (referenceDirection && actualDirection) {
            // Pivot point is the first keypoint (e.g., upper_torso)
            const pivot = new THREE.Vector3(...points[0]);

            const refDir = referenceDirection.clone().normalize();
            const actualDir = actualDirection.clone().normalize();

            // Large scale so lines extend visually
            const lineScale = 10;

            // Reference line from pivot in reference direction
            const referenceLineStart = pivot.clone().add(refDir.clone().multiplyScalar(-lineScale));
            const referenceLineEnd = pivot.clone().add(refDir.clone().multiplyScalar(lineScale));

            // Directional line from pivot in actual direction
            const directionalLineStart = pivot.clone().add(actualDir.clone().multiplyScalar(-lineScale));
            const directionalLineEnd = pivot.clone().add(actualDir.clone().multiplyScalar(lineScale));

            const triangleScale = 0.15;
            const triangleVertices = [
                pivot.clone(),
                pivot.clone().add(actualDir.clone().multiplyScalar(triangleScale)),
                pivot.clone().add(refDir.clone().multiplyScalar(triangleScale)),
            ];

            return {
                directionalLine: [directionalLineStart, directionalLineEnd],
                referenceLine: [referenceLineStart, referenceLineEnd],
                triangleBuffer: createTriangleBuffer(triangleVertices),
            };
        }

        const refDir = getRefDir(referenceDirection, DEFAULT_REFERENCE_DIRECTIONS.tilt);
        const actualDir: [number, number, number] = actualDirection
            ? [actualDirection.x, actualDirection.y, actualDirection.z]
            : [0, 1, 0];

        const [directionalLineStart, directionalLineEnd] = createDirectionalLine(
            [points[2], points[1]],
            actualDir,
            true,
        );
        const [referenceLineStart, referenceLineEnd] = createMidPointDirectionalLine(
            [points[1], points[2]],
            refDir,
            false,
        );

        const triangleLerp = 0.06;
        const triangleVertices = [
            referenceLineStart,
            new THREE.Vector3().lerpVectors(referenceLineStart, directionalLineEnd, triangleLerp),
            new THREE.Vector3().lerpVectors(referenceLineStart, referenceLineEnd, triangleLerp),
        ];

        return {
            directionalLine: [directionalLineStart, directionalLineEnd],
            referenceLine: [referenceLineStart, referenceLineEnd],
            triangleBuffer: createTriangleBuffer(triangleVertices),
        };
    }

    // Visualization consists of 'connected' lines
    const triangleVertices = [
        new THREE.Vector3(...points[0]),
        new THREE.Vector3(...points[1]),
        new THREE.Vector3(...points[2]),
    ];

    return {
        directionalLine: [
            new THREE.Vector3(...points[0]),
            new THREE.Vector3(...points[1]),
            new THREE.Vector3(...points[2]),
        ],
        referenceLine: [triangleVertices[0], triangleVertices[2]],
        triangleBuffer: createTriangleBuffer(triangleVertices),
    };
};

// Three point bend angle
const calculateThreePointBendLines = (
    points: PointMatrix,
    type: ConnectionType,
    referenceDirection?: THREE.Vector3,
    actualDirection?: THREE.Vector3,
): VisualizationCalculation => {
    // Visualization consists of 'extended' lines
    if (type === 'extended') {
        // When Euler-derived directions are provided, use them for both lines
        if (referenceDirection && actualDirection) {
            // Pivot point is the first keypoint (e.g., upper_torso)
            const pivot = new THREE.Vector3(...points[0]);

            const refDir = referenceDirection.clone().normalize();
            const actualDir = actualDirection.clone().normalize();

            // Large scale so lines extend visually
            const lineScale = 10;

            // Reference line from pivot in reference direction
            const referenceLineStart = pivot.clone().add(refDir.clone().multiplyScalar(-lineScale));
            const referenceLineEnd = pivot.clone().add(refDir.clone().multiplyScalar(lineScale));

            // Directional line from pivot in actual direction
            const directionalLineStart = pivot.clone().add(actualDir.clone().multiplyScalar(-lineScale));
            const directionalLineEnd = pivot.clone().add(actualDir.clone().multiplyScalar(lineScale));

            const triangleScale = 0.15;
            const triangleVertices = [
                pivot.clone(),
                pivot.clone().add(actualDir.clone().multiplyScalar(triangleScale)),
                pivot.clone().add(refDir.clone().multiplyScalar(triangleScale)),
            ];

            return {
                directionalLine: [directionalLineStart, directionalLineEnd],
                referenceLine: [referenceLineStart, referenceLineEnd],
                triangleBuffer: createTriangleBuffer(triangleVertices),
            };
        }

        const defaultBendDir = new THREE.Vector3(0, 0, -1);
        const refDir = referenceDirection
            ? ([-referenceDirection.x, -referenceDirection.y, -referenceDirection.z] as [number, number, number])
            : ([defaultBendDir.x, defaultBendDir.y, defaultBendDir.z] as [number, number, number]);

        const actualDir: [number, number, number] = actualDirection
            ? [-actualDirection.x, -actualDirection.y, -actualDirection.z]
            : [0, 1, 0];

        const [directionalLineStart, directionalLineEnd] = createDirectionalLine(
            [points[1], points[0]],
            actualDir,
            true,
        );
        const [referenceLineStart, referenceLineEnd] = createDirectionalLine([points[0]], refDir);

        const triangleLerp = 0.06;
        const triangleVertices = [
            referenceLineStart,
            new THREE.Vector3().lerpVectors(referenceLineStart, directionalLineStart, triangleLerp),
            new THREE.Vector3().lerpVectors(referenceLineStart, referenceLineEnd, triangleLerp),
        ];

        return {
            directionalLine: [directionalLineStart, directionalLineEnd],
            referenceLine: [referenceLineStart, referenceLineEnd],
            triangleBuffer: createTriangleBuffer(triangleVertices),
        };
    }

    // Visualization consists of 'connected' lines
    const triangleVertices = [
        new THREE.Vector3(...points[0]),
        new THREE.Vector3(...points[1]),
        new THREE.Vector3(...points[2]),
    ];

    return {
        directionalLine: [
            new THREE.Vector3(...points[0]),
            new THREE.Vector3(...points[1]),
            new THREE.Vector3(...points[2]),
        ],
        referenceLine: [triangleVertices[0], triangleVertices[2]],
        triangleBuffer: createTriangleBuffer(triangleVertices),
    };
};

// Three point rotation angle
const calculateThreePointRotationLines = (
    points: PointMatrix,
    type: ConnectionType,
    referenceDirection?: THREE.Vector3,
    actualDirection?: THREE.Vector3,
): VisualizationCalculation => {
    // Visualization consists of 'extended' lines
    if (type === 'extended') {
        const refDir = getRefDir(referenceDirection, DEFAULT_REFERENCE_DIRECTIONS.rotation);
        const headPos = new THREE.Vector3(...points[0]);

        // For intrinsic mode (when referenceDirection is provided):
        // - Both lines centered on head
        // - Reference line shows local Y direction (zero rotation)
        // - Directional line shows actual direction (from Euler angles)
        // - Lines extend to frustum edges
        if (referenceDirection) {
            // Use actualDirection from Euler angles (ensures displayed angle matches measurement)
            // Fallback to ear-to-ear from keypoints if actualDirection is not provided
            let actualDir: THREE.Vector3;

            if (actualDirection) {
                actualDir = actualDirection.clone().normalize();
            } else {
                const leftEar = new THREE.Vector3(...points[1]);
                const rightEar = new THREE.Vector3(...points[2]);
                actualDir = leftEar.clone().sub(rightEar).normalize();
            }

            // Large scale so lines extend visually
            const lineScale = 10;
            const directionalLineStart = headPos.clone().add(actualDir.clone().multiplyScalar(-lineScale));
            const directionalLineEnd = headPos.clone().add(actualDir.clone().multiplyScalar(lineScale));

            const refDirVec = new THREE.Vector3(...refDir).normalize();
            const referenceLineStart = headPos.clone().add(refDirVec.clone().multiplyScalar(-lineScale));
            const referenceLineEnd = headPos.clone().add(refDirVec.clone().multiplyScalar(lineScale));

            const triangleScale = 0.15;
            const triangleVertices = [
                headPos.clone(),
                headPos.clone().add(actualDir.clone().multiplyScalar(triangleScale)),
                headPos.clone().add(refDirVec.clone().multiplyScalar(triangleScale)),
            ];

            return {
                directionalLine: [directionalLineStart, directionalLineEnd],
                referenceLine: [referenceLineStart, referenceLineEnd],
                triangleBuffer: createTriangleBuffer(triangleVertices),
            };
        }

        // Without referenceDirection we use global reference lines
        const [directionalLineStart, directionalLineEnd] = createDirectionalLine(
            [points[2], points[1]],
            [0, 1, 0],
            true,
        );
        const [referenceLineStart, referenceLineEnd] = createDirectionalLine([points[0]], refDir, false);

        const adjustedLine1Start = directionalLineStart.clone().setZ(referenceLineStart.z);
        const adjustedLine1End = directionalLineEnd.clone().setZ(referenceLineEnd.z);

        const triangleLerp = 0.06;
        const triangleVertices = [
            referenceLineStart,
            new THREE.Vector3().lerpVectors(referenceLineStart, adjustedLine1End, triangleLerp),
            new THREE.Vector3().lerpVectors(referenceLineStart, referenceLineEnd, triangleLerp),
        ];

        return {
            directionalLine: [adjustedLine1Start, adjustedLine1End],
            referenceLine: [referenceLineStart, referenceLineEnd],
            triangleBuffer: createTriangleBuffer(triangleVertices),
        };
    }

    const triangleVertices = [
        new THREE.Vector3(...points[0]),
        new THREE.Vector3(...points[1]),
        new THREE.Vector3(...points[2]),
    ];

    return {
        directionalLine: [
            new THREE.Vector3(...points[0]),
            new THREE.Vector3(...points[1]),
            new THREE.Vector3(...points[2]),
        ],
        referenceLine: [triangleVertices[0], triangleVertices[2]],
        triangleBuffer: createTriangleBuffer(triangleVertices),
    };
};

function AngleVisualizationRenderer({
    currentFrame,
    directionalLine,
    referenceLine,
    triangleBuffer,
    opacity = 1,
    showFill = false,
}: {
    currentFrame: number;
    directionalLine: THREE.Vector3[];
    referenceLine: THREE.Vector3[];
    triangleBuffer: Float32Array<ArrayBufferLike>;
    opacity?: number;
    showFill?: boolean;
}) {
    return (
        <group>
            <Line
                color={colors.green[500]}
                lineWidth={2}
                points={directionalLine}
                opacity={opacity}
                depthWrite={false}
            />

            <Line
                color={colors.yellow[500]}
                lineWidth={2}
                points={referenceLine}
                dashed
                segments={false}
                dashScale={64}
                opacity={opacity}
                depthWrite={false}
            />

            {showFill && (
                <mesh>
                    <bufferGeometry key={currentFrame}>
                        <bufferAttribute
                            attach="attributes-position"
                            args={[triangleBuffer, 3]}
                            count={3}
                            array={triangleBuffer}
                            itemSize={3}
                        />
                        <bufferAttribute
                            attach="index"
                            args={[TRIANGLE_INDICES, 1]}
                            array={TRIANGLE_INDICES}
                            count={3}
                            itemSize={1}
                        />
                    </bufferGeometry>

                    <meshBasicMaterial
                        color={colors.yellow[400]}
                        transparent
                        opacity={0.1 * opacity}
                        side={THREE.DoubleSide}
                        depthWrite={false}
                    />
                </mesh>
            )}
        </group>
    );
}

type DirectionAngleProps = {
    pivot: THREE.Vector3;
    referenceDirection: THREE.Vector3;
    actualDirection: THREE.Vector3;
    currentFrame: number;
    opacity?: number;
    showFill?: boolean;
};

const DirectionAngle = ({
    pivot,
    referenceDirection,
    actualDirection,
    currentFrame,
    opacity = 1,
    showFill = false,
}: DirectionAngleProps) => {
    const { directionalLine, referenceLine, triangleBuffer } = useMemo(
        () => calculateDirectionAngleLines(pivot, referenceDirection, actualDirection),
        [actualDirection, pivot, referenceDirection],
    );

    return (
        <AngleVisualizationRenderer
            currentFrame={currentFrame}
            directionalLine={directionalLine}
            referenceLine={referenceLine}
            triangleBuffer={triangleBuffer}
            opacity={opacity}
            showFill={showFill}
        />
    );
};

type CalculatorFn = (
    points: PointMatrix,
    type: ConnectionType,
    referenceDirection?: THREE.Vector3,
    actualDirection?: THREE.Vector3,
) => VisualizationCalculation;

function createAngleVisualizer(calculator: CalculatorFn) {
    return function AngleVisualizer({
        type,
        points,
        currentFrame,
        opacity = 1,
        showFill = false,
        referenceDirection,
        actualDirection,
    }: {
        type: ConnectionType;
        points: PointMatrix;
        currentFrame: number;
        opacity?: number;
        showFill?: boolean;
        referenceDirection?: THREE.Vector3;
        actualDirection?: THREE.Vector3;
    }) {
        const { directionalLine, referenceLine, triangleBuffer } = useMemo(
            () => calculator(points, type, referenceDirection, actualDirection) as VisualizationCalculation,
            [points, type, referenceDirection, actualDirection],
        );

        return (
            <AngleVisualizationRenderer
                currentFrame={currentFrame}
                directionalLine={directionalLine}
                referenceLine={referenceLine}
                triangleBuffer={triangleBuffer}
                opacity={opacity}
                showFill={showFill}
            />
        );
    };
}

// Wrapper functions to match the expected signature
const wrapTwoPointCalculator =
    (
        fn: (
            points: PointMatrix,
            referenceDirection?: THREE.Vector3,
            actualDirection?: THREE.Vector3,
        ) => VisualizationCalculation,
    ): CalculatorFn =>
    (points, _type, referenceDirection, actualDirection) =>
        fn(points, referenceDirection, actualDirection);

const TwoPointRotationAngle = createAngleVisualizer(wrapTwoPointCalculator(calculateTwoPointRotationLines));
const TwoPointTiltAngle = createAngleVisualizer(wrapTwoPointCalculator(calculateTwoPointTiltLines));
const TwoPointBendAngle = createAngleVisualizer(wrapTwoPointCalculator(calculateTwoPointBendLines));
const ThreePointTiltAngle = createAngleVisualizer(calculateThreePointTiltLines);
const ThreePointBendAngle = createAngleVisualizer(calculateThreePointBendLines);
const ThreePointRotationAngle = createAngleVisualizer(calculateThreePointRotationLines);

export function AngleVisualization({
    type,
    frame = 0,
    skeleton,
    targetPoints,
    keypointIdToName,
    currentFocus = null,
    opacity = 1,
    showFill = false,
    pivot,
    referenceDirection,
    actualDirection,
}: AngleVisualizationProps) {
    if (!currentFocus) {
        return null;
    }

    /**
     * Explicit geometry takes precedence over skeleton-based angle inference.
     *
     * In particular, this prevents a scalar axisIndex of 0 from accidentally
     * selecting TwoPointTiltAngle for measurements such as club angles.
     */
    if (pivot && referenceDirection && actualDirection) {
        return (
            <DirectionAngle
                pivot={pivot}
                referenceDirection={referenceDirection}
                actualDirection={actualDirection}
                currentFrame={frame}
                opacity={opacity}
                showFill={showFill}
            />
        );
    }

    const points = targetPoints
        .map((name) => getKeypointId(name, keypointIdToName))
        .map((point) => getSafePoint(skeleton, point, frame));

    if (!Array.isArray(points) || !points.length) {
        return null;
    }

    const props: VisualizationProps = {
        type,
        points,
        currentFrame: frame,
        opacity,
        showFill,
        referenceDirection,
        actualDirection,
    };

    if (points.length === 2) {
        if (currentFocus.axis === 0) {
            return <TwoPointTiltAngle {...props} />;
        }

        if (currentFocus.axis === 1) {
            return <TwoPointBendAngle {...props} />;
        }

        if (currentFocus.axis === 2) {
            return <TwoPointRotationAngle {...props} />;
        }
    }

    if (points.length === 3) {
        if (currentFocus.axis === 0) {
            return <ThreePointTiltAngle {...props} />;
        }

        if (currentFocus.axis === 1) {
            return <ThreePointBendAngle {...props} />;
        }

        if (currentFocus.axis === 2) {
            return <ThreePointRotationAngle {...props} />;
        }
    }

    return null;
}

export const MemoAngleVisualization = memo(AngleVisualization);
